import { useEffect } from "react";
import { Image } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { BlurView } from "expo-blur";
import { FadeInDown } from "react-native-reanimated";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LIcon, { IconName } from "@components/LIcon";
import LPressable from "@components/LPressable";
import { fonts } from "@theme";
import { useTabBarHeight } from "@hooks/useTabBarHeight";
import { router } from "expo-router";
import { StreamingService } from "./constants";
import { trackOnb } from "./analytics/trackOnboarding";
import { useSetupMusicImport } from "./useSetupMusicImport";

import LGradient from "@components/LGradient";
import { useTopBarHeight } from "@hooks/useTopBarHeight";
import { useStableWindowDimensions } from "@hooks/useStableWindowDimensions";
import { getViewPort } from "@utils/web/config";
const PLATFORM_BUTTONS: {
  type: StreamingService;
  name: string;
  icon: IconName;
}[] = [
  { type: "spotify", name: "Spotify", icon: "spotify" },
  { type: "appleMusic", name: "Apple Music", icon: "apple_music" },
  { type: "soundCloud", name: "SoundCloud", icon: "soundcloud" },
];

export const SetupScreen = () => {
  return <BringYourMusic />;
};

// --- Bring Your Music ---

const BringYourMusic = () => {
  const { initialize, start, busy, message, selectedService } =
    useSetupMusicImport();

  useEffect(() => {
    trackOnb("signup", "bring-music", "view");
    initialize().catch(console.error);
  }, [initialize]);

  const tabBarHeight = useTabBarHeight();
  const _topBarHeight = useTopBarHeight();
  const dimensions = useStableWindowDimensions();
  const { width: WINDOW_WIDTH, height: WINDOW_HEIGHT } =
    getViewPort(dimensions);
  const topBarHeight = _topBarHeight - (WINDOW_HEIGHT / 100) * 3.5;

  return (
    <LBox testID="bring-music" flex={1}>
      {/* Background image — stop above tab bar */}
      <Image
        source={require("@assets/onboarding/welcome.webp")}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: tabBarHeight,
          width: "100%",
          height: undefined,
        }}
        resizeMode="cover"
      />
      {/* Top gradient (matches feed) */}
      <LBox position="absolute">
        <LBox
          position="absolute"
          top={0}
          height={topBarHeight}
          width={WINDOW_WIDTH}
          backgroundColor="black"
        />
        <LGradient
          colors={["black", "transparent"]}
          locations={[0, 1]}
          top={topBarHeight}
          height={50}
          width={WINDOW_WIDTH}
        />
      </LBox>
      {/* Bottom gradient overlay */}
      <LinearGradient
        colors={["transparent", "rgba(0,0,0,0.85)", "#000"]}
        locations={[0, 0.8, 1]}
        style={{
          position: "absolute",
          bottom: tabBarHeight,
          left: 0,
          right: 0,
          height: "45%",
        }}
        pointerEvents="none"
      />
      {/* Black fill behind tab bar */}
      <LBox
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        style={{ height: tabBarHeight, backgroundColor: "#000" }}
      />

      {/* Content pinned to bottom */}
      <LBox
        flex={1}
        justifyContent="flex-end"
        px="m1"
        style={{ paddingBottom: tabBarHeight + 32 }}
      >
        <LBox gap="s3" mb="m1" entering={FadeInDown.duration(400).delay(200)}>
          <LText
            fontSize={32}
            fontFamily={fonts.semiBold}
            color="white"
            style={{
              letterSpacing: -0.64,
              textShadowColor: "rgba(0,0,0,0.8)",
              textShadowOffset: { width: 0, height: 2 },
              textShadowRadius: 8,
            }}
            lineHeight={35}
          >
            {"Bring your music\nwith you"}
          </LText>
          <LText
            fontSize={14}
            fontFamily={fonts.medium}
            color="whiteTranslucent60"
            style={{
              textShadowColor: "rgba(0,0,0,0.8)",
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 6,
            }}
          >
            Sign in to your streaming service and allow access to import your
            playlists for free.
          </LText>
        </LBox>

        <LBox
          flexDirection="row"
          gap="s3"
          mb="m1"
          entering={FadeInDown.duration(400).delay(400)}
          style={{
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowRadius: 12,
            shadowOpacity: 0.6,
            elevation: 8,
          }}
        >
          {PLATFORM_BUTTONS.map((platform) => (
            <PlatformButton
              key={platform.type}
              platform={platform}
              disabled={busy}
              onPress={() => {
                start(platform.type).catch(console.error);
              }}
            />
          ))}
        </LBox>

        {(busy || message) && (
          <LBox gap="s3" mb="m1">
            <LText color="white" fontSize={14} accessibilityLiveRegion="polite">
              {busy ? "Connecting and starting your import…" : message}
            </LText>
            {!busy && selectedService && (
              <LPressable
                sentryEventName={null}
                accessibilityRole="button"
                onPress={() => {
                  start(selectedService).catch(console.error);
                }}
                style={{ minHeight: 44, justifyContent: "center" }}
              >
                <LText color="white" fontSize={16}>
                  Try{" "}
                  {
                    PLATFORM_BUTTONS.find((p) => p.type === selectedService)
                      ?.name
                  }{" "}
                  again
                </LText>
              </LPressable>
            )}
          </LBox>
        )}

        <LBox
          gap="s3"
          alignItems="center"
          entering={FadeInDown.duration(400).delay(600)}
        >
          <LPressable
            sentryEventName={null}
            disabled={busy}
            onPress={() => {
              trackOnb("signup", "bring-music", "start-fresh-pressed");
              router.navigate("/onboarding/pick-genres");
            }}
          >
            <LText
              fontSize={12}
              fontFamily={fonts.medium}
              style={{ color: "rgba(255,255,255,0.8)", letterSpacing: -0.24 }}
              textAlign="center"
            >
              or start fresh
            </LText>
          </LPressable>
          <LPressable
            fadeEnabled
            sentryEventName={null}
            disabled={busy}
            onPress={() => {
              trackOnb("signup", "bring-music", "pick-genres-pressed");
              router.navigate("/onboarding/pick-genres");
            }}
          >
            <BlurView
              intensity={35}
              tint="light"
              style={{
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 16,
                overflow: "hidden",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 4 },
                shadowRadius: 12,
                shadowOpacity: 0.6,
                elevation: 8,
              }}
            >
              <LText
                fontSize={12}
                fontFamily={fonts.medium}
                color="white"
                style={{ letterSpacing: -0.24 }}
              >
                Pick your music genres
              </LText>
            </BlurView>
          </LPressable>
        </LBox>
      </LBox>
    </LBox>
  );
};

// --- Platform Button ---

const PlatformButton = ({
  platform,
  onPress,
  disabled,
}: {
  platform: (typeof PLATFORM_BUTTONS)[number];
  onPress: () => void;
  disabled: boolean;
}) => {
  return (
    <LPressable
      fadeEnabled
      sentryEventName={null}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ disabled, busy: disabled }}
      style={{ flex: 1 / 3, opacity: disabled ? 0.5 : 1 }}
    >
      <BlurView
        intensity={30}
        tint="light"
        style={{
          borderRadius: 12,
          paddingVertical: 12,
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          overflow: "hidden",
        }}
      >
        <LIcon name={platform.icon} size={32} />
        <LText
          fontSize={12}
          fontFamily={fonts.medium}
          color="white"
          textAlign="center"
          style={{ letterSpacing: -0.24 }}
        >
          {platform.name}
        </LText>
      </BlurView>
    </LPressable>
  );
};
